import { useState } from 'react';
import {
    BarChart3, Download, Calendar,
    Building2, BedDouble, Users, TrendingUp, Search,
    Eye, Printer, RefreshCw,
    CheckCircle, Clock, AlertCircle, Hotel, Home,
    Shirt, Wrench, DollarSign, PieChart, FileSpreadsheet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
    BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

interface AccommodationReportsPageProps {
    onNavigate?: (page: string) => void;
}

const COLORS = ['#5B9BD5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const occupancyData = [
    { month: 'Jan', karbala: 85, najaf: 72, mecca: 90 },
    { month: 'Feb', karbala: 78, najaf: 80, mecca: 88 },
    { month: 'Mar', karbala: 92, najaf: 85, mecca: 95 },
    { month: 'Apr', karbala: 88, najaf: 76, mecca: 91 },
    { month: 'May', karbala: 95, najaf: 92, mecca: 98 },
    { month: 'Jun', karbala: 82, najaf: 88, mecca: 86 },
];

const roomTypeData = [
    { name: 'Deluxe Double', value: 42 },
    { name: 'Standard Twin', value: 28 },
    { name: 'Shared Gents', value: 18 },
    { name: 'Shared Ladies', value: 12 },
];

const checkInOutData = [
    { date: 'Sep 18', checkIn: 45, checkOut: 12 },
    { date: 'Sep 19', checkIn: 38, checkOut: 20 },
    { date: 'Sep 20', checkIn: 52, checkOut: 35 },
    { date: 'Sep 21', checkIn: 30, checkOut: 48 },
    { date: 'Sep 22', checkIn: 25, checkOut: 60 },
    { date: 'Sep 23', checkIn: 18, checkOut: 42 },
];

const reports = [
    {
        id: 'occupancy',
        title: 'Occupancy Report',
        description: 'Room-wise and property-wise occupancy rates with trends',
        icon: <BarChart3 className="h-5 w-5" />,
        color: 'bg-blue-50',
        iconColor: 'text-blue-600',
        category: 'Operations',
        lastGenerated: '2 hours ago',
        badge: 'Popular'
    },
    {
        id: 'room-allocation',
        title: 'Room Allocation Report',
        description: 'Detailed breakdown of applicant room assignments per tour',
        icon: <BedDouble className="h-5 w-5" />,
        color: 'bg-indigo-50',
        iconColor: 'text-indigo-600',
        category: 'Operations',
        lastGenerated: '1 hour ago',
        badge: null
    },
    {
        id: 'checkin-checkout',
        title: 'Check-in / Check-out Report',
        description: 'Daily arrivals and departures across all properties',
        icon: <CheckCircle className="h-5 w-5" />,
        color: 'bg-green-50',
        iconColor: 'text-green-600',
        category: 'Operations',
        lastGenerated: '30 min ago',
        badge: null
    },
    {
        id: 'applicant-summary',
        title: 'Applicant Accommodation Summary',
        description: 'Per-applicant accommodation history, room details and stay duration',
        icon: <Users className="h-5 w-5" />,
        color: 'bg-purple-50',
        iconColor: 'text-purple-600',
        category: 'Applicants',
        lastGenerated: '3 hours ago',
        badge: null
    },
    {
        id: 'tour-wise',
        title: 'Tour-wise Accommodation Report',
        description: 'Complete accommodation breakdown per tour with costs and room types',
        icon: <Hotel className="h-5 w-5" />,
        color: 'bg-cyan-50',
        iconColor: 'text-cyan-600',
        category: 'Tours',
        lastGenerated: '5 hours ago',
        badge: null
    },
    {
        id: 'property-wise',
        title: 'Property-wise Report',
        description: 'Revenue, occupancy and guest statistics per property',
        icon: <Building2 className="h-5 w-5" />,
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
        category: 'Properties',
        lastGenerated: '1 day ago',
        badge: null
    },
    {
        id: 'vacancy',
        title: 'Vacancy Report',
        description: 'Available rooms by type, property and date range',
        icon: <Home className="h-5 w-5" />,
        color: 'bg-rose-50',
        iconColor: 'text-rose-600',
        category: 'Operations',
        lastGenerated: '4 hours ago',
        badge: 'Live'
    },
    {
        id: 'revenue',
        title: 'Revenue Report',
        description: 'Accommodation revenue breakdown by property, room type and tour',
        icon: <DollarSign className="h-5 w-5" />,
        color: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        category: 'Finance',
        lastGenerated: '6 hours ago',
        badge: null
    },
    {
        id: 'housekeeping-status',
        title: 'Housekeeping Status Report',
        description: 'Room cleaning status, staff assignments and completion rates',
        icon: <Shirt className="h-5 w-5" />,
        color: 'bg-pink-50',
        iconColor: 'text-pink-600',
        category: 'Housekeeping',
        lastGenerated: '45 min ago',
        badge: null
    },
    {
        id: 'maintenance',
        title: 'Maintenance Report',
        description: 'Open, in-progress and resolved maintenance requests per property',
        icon: <Wrench className="h-5 w-5" />,
        color: 'bg-orange-50',
        iconColor: 'text-orange-600',
        category: 'Maintenance',
        lastGenerated: '2 hours ago',
        badge: null
    },
    {
        id: 'laundry',
        title: 'Laundry Report',
        description: 'Laundry pickup, processing and delivery status per room and applicant',
        icon: <Shirt className="h-5 w-5" />,
        color: 'bg-teal-50',
        iconColor: 'text-teal-600',
        category: 'Housekeeping',
        lastGenerated: '1 hour ago',
        badge: null
    },
    {
        id: 'room-type-wise',
        title: 'Room Type Analysis',
        description: 'Demand, utilization and revenue analysis by room type',
        icon: <PieChart className="h-5 w-5" />,
        color: 'bg-violet-50',
        iconColor: 'text-violet-600',
        category: 'Analysis',
        lastGenerated: '12 hours ago',
        badge: null
    },
    {
        id: 'extended-stays',
        title: 'Extended Stay Report',
        description: 'Applicants with stay extensions, reasons and approval status',
        icon: <Clock className="h-5 w-5" />,
        color: 'bg-sky-50',
        iconColor: 'text-sky-600',
        category: 'Operations',
        lastGenerated: '8 hours ago',
        badge: null
    },
    {
        id: 'special-requests',
        title: 'Special Requests Report',
        description: 'All guest special requests, their status and resolution time',
        icon: <AlertCircle className="h-5 w-5" />,
        color: 'bg-yellow-50',
        iconColor: 'text-yellow-600',
        category: 'Operations',
        lastGenerated: '3 hours ago',
        badge: null
    },
    {
        id: 'arrival-departure',
        title: 'Arrival & Departure Forecast',
        description: 'Upcoming arrivals and departures for the next 7 days',
        icon: <TrendingUp className="h-5 w-5" />,
        color: 'bg-lime-50',
        iconColor: 'text-lime-600',
        category: 'Forecast',
        lastGenerated: '1 hour ago',
        badge: 'New'
    },
    {
        id: 'family-grouping',
        title: 'Family Grouping Report',
        description: 'Family units, their room assignments and proximity preferences',
        icon: <Users className="h-5 w-5" />,
        color: 'bg-fuchsia-50',
        iconColor: 'text-fuchsia-600',
        category: 'Applicants',
        lastGenerated: '4 hours ago',
        badge: null
    },
];

const categories = ['All', 'Operations', 'Applicants', 'Tours', 'Properties', 'Finance', 'Housekeeping', 'Maintenance', 'Analysis', 'Forecast'];

const mockOccupancyTable = [
    { property: 'Al Kawthar Hotel, Karbala', roomType: 'Deluxe Double', total: 40, occupied: 38, vacant: 2, occupancy: 95 },
    { property: 'Al Kawthar Hotel, Karbala', roomType: 'Standard Twin', total: 60, occupied: 52, vacant: 8, occupancy: 87 },
    { property: 'Al Kawthar Hotel, Karbala', roomType: 'Shared Gents', total: 20, occupied: 20, vacant: 0, occupancy: 100 },
    { property: 'Najaf Grand Hotel, Najaf', roomType: 'Deluxe Double', total: 50, occupied: 42, vacant: 8, occupancy: 84 },
    { property: 'Najaf Grand Hotel, Najaf', roomType: 'Shared Ladies', total: 15, occupied: 14, vacant: 1, occupancy: 93 },
];

export function AccommodationReportsPage({ onNavigate }: AccommodationReportsPageProps) {
    const [activeReport, setActiveReport] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [dateFrom, setDateFrom] = useState('2025-09-18');
    const [dateTo, setDateTo] = useState('2025-09-27');
    const [selectedProperty, setSelectedProperty] = useState('All');
    const [generatingReport, setGeneratingReport] = useState<string | null>(null);

    const filteredReports = reports.filter(r => {
        const matchesCategory = selectedCategory === 'All' || r.category === selectedCategory;
        const matchesSearch = r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            r.description.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const handleGenerate = (reportId: string) => {
        setGeneratingReport(reportId);
        setTimeout(() => {
            setGeneratingReport(null);
            setActiveReport(reportId);
        }, 1200);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b px-6 py-4">
                <div className="max-w-[1400px] mx-auto flex items-center justify-between">
                    <div>
                        <h1 className="flex items-center gap-2 text-xl font-semibold" style={{ color: '#5B9BD5' }}>
                            <FileSpreadsheet className="h-5 w-5" />
                            Accommodation Reports
                        </h1>
                        <p className="text-sm text-muted-foreground mt-0.5">Generate and export comprehensive accommodation reports</p>
                    </div>
                    <div className="flex flex-wrap justify-end items-center gap-2">
                        <Button variant="outline" size="sm" className="gap-1.5">
                            <RefreshCw className="h-3.5 w-3.5" />
                            Refresh All
                        </Button>
                        <Button size="sm" className="gap-1.5" style={{ background: '#5B9BD5' }}>
                            <Download className="h-3.5 w-3.5" />
                            Bulk Export
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-6 py-6 space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {[
                        { label: 'Total Rooms', value: '485', sub: 'Across all properties', color: 'text-blue-600', bg: 'bg-blue-50' },
                        { label: 'Occupied', value: '421', sub: '86.8% occupancy rate', color: 'text-green-600', bg: 'bg-green-50' },
                        { label: 'Vacant', value: '64', sub: '13.2% availability', color: 'text-amber-600', bg: 'bg-amber-50' },
                        { label: 'Total Guests', value: '1,247', sub: 'Currently checked-in', color: 'text-purple-600', bg: 'bg-purple-50' },
                    ].map(stat => (
                        <Card key={stat.label}
                            className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                            <CardContent className="p-4">

                                {/* Top Row */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                                        <BarChart3 className={`h-5 w-5 ${stat.color}`} />
                                    </div>
                                    <div className={`text-lg sm:text-xl lg:text-2xl font-bold ${stat.color}`}>
                                        {stat.value}
                                    </div>
                                </div>

                                {/* Content */}
                                <div>
                                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                                        {stat.label}
                                    </h3>
                                    <p className="mt-1 text-xs sm:text-sm text-gray-500">
                                        {stat.sub}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Filters */}
                <Card className="bg-white border-0 shadow-sm">
                    <CardContent className="p-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="relative flex-1 min-w-[220px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    placeholder="Search reports..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="pl-9 h-9"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto_auto_1fr] gap-2 items-center">
                                <Calendar className="h-4 w-4 text-gray-400 hidden md:block" />
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                                />
                                <span className="hidden md:block text-gray-400 text-sm">to</span>
                                <Calendar className="h-4 w-4 text-gray-400 hidden md:block" />
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                                />
                            </div>
                            <select
                                value={selectedProperty}
                                onChange={e => setSelectedProperty(e.target.value)}
                                className="border border-gray-200 rounded-md px-3 py-1.5 text-sm"
                            >
                                <option value="All">All Properties</option>
                                <option value="karbala">Al Kawthar Hotel, Karbala</option>
                                <option value="najaf">Najaf Grand Hotel, Najaf</option>
                            </select>
                        </div>
                        {/* Category tabs */}
                        <div className="flex flex-wrap gap-4 mt-4">
                            {categories.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedCategory === cat
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Quick Charts */}
                {activeReport === null && (
                    <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-4">
                        <Card className="bg-white border-0 shadow-sm col-span-2">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm">Occupancy Trend (%) by Property</CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 pt-0">
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={occupancyData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} domain={[60, 100]} />
                                        <Tooltip />
                                        <Legend />
                                        <Line type="monotone" dataKey="karbala" stroke="#5B9BD5" strokeWidth={2} dot={false} name="Karbala" />
                                        <Line type="monotone" dataKey="najaf" stroke="#10B981" strokeWidth={2} dot={false} name="Najaf" />
                                        <Line type="monotone" dataKey="mecca" stroke="#F59E0B" strokeWidth={2} dot={false} name="Mecca" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                        <Card className="sm:w-full bg-white border-0 shadow-sm">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm">Room Type Distribution</CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 pt-0">
                                <ResponsiveContainer width="100%" height={200}>
                                    <RechartsPie>
                                        <Pie
                                            data={roomTypeData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={80}
                                            dataKey="value"
                                            label={({ name, value }) => `${value}%`}
                                            labelLine={false}
                                        >
                                            {roomTypeData.map((_, i) => (
                                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </RechartsPie>
                                </ResponsiveContainer>
                                <div className="space-y-1 mt-2">
                                    {roomTypeData.map((d, i) => (
                                        <div key={d.name} className="flex items-center justify-between text-xs">
                                            <div className="flex items-center gap-1.5">
                                                <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i] }} />
                                                <span className="text-gray-600">{d.name}</span>
                                            </div>
                                            <span className="font-medium">{d.value}%</span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Active Report View */}
                {activeReport === 'occupancy' && (
                    <Card className="bg-white border-0 shadow-sm">
                        <CardHeader className="border-b">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <CardTitle className="text-sm lg:text-base">Occupancy Report — {dateFrom} to {dateTo}</CardTitle>
                                <div className="flex flex-wrap gap-2 m-2 lg:m-0">
                                    <Button variant="outline" size="sm" className="gap-1"><Printer className="h-3.5 w-3.5" />Print</Button>
                                    <Button variant="outline" size="sm" className="gap-1"><Download className="h-3.5 w-3.5" />Excel</Button>
                                    <Button variant="outline" size="sm" onClick={() => setActiveReport(null)}>Close</Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-4">
                            <div className="mb-4">
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={occupancyData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="karbala" fill="#5B9BD5" name="Karbala" radius={[3, 3, 0, 0]} />
                                        <Bar dataKey="najaf" fill="#10B981" name="Najaf" radius={[3, 3, 0, 0]} />
                                        <Bar dataKey="mecca" fill="#F59E0B" name="Mecca" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b bg-gray-50">
                                            <th className="text-left py-2 px-3 text-xs text-gray-600">Property</th>
                                            <th className="text-left py-2 px-3 text-xs text-gray-600">Room Type</th>
                                            <th className="text-center py-2 px-3 text-xs text-gray-600">Total Rooms</th>
                                            <th className="text-center py-2 px-3 text-xs text-gray-600">Occupied</th>
                                            <th className="text-center py-2 px-3 text-xs text-gray-600">Vacant</th>
                                            <th className="text-center py-2 px-3 text-xs text-gray-600">Occupancy %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mockOccupancyTable.map((row, i) => (
                                            <tr key={i} className="border-b hover:bg-gray-50">
                                                <td className="py-2 px-3 text-xs text-gray-700">{row.property}</td>
                                                <td className="py-2 px-3 text-xs text-gray-700">{row.roomType}</td>
                                                <td className="py-2 px-3 text-xs text-center">{row.total}</td>
                                                <td className="py-2 px-3 text-xs text-center text-green-600 font-medium">{row.occupied}</td>
                                                <td className="py-2 px-3 text-xs text-center text-amber-600 font-medium">{row.vacant}</td>
                                                <td className="py-2 px-3 text-center">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                                            <div
                                                                className={`h-1.5 rounded-full ${row.occupancy >= 90 ? 'bg-green-500' : row.occupancy >= 75 ? 'bg-blue-500' : 'bg-amber-500'}`}
                                                                style={{ width: `${row.occupancy}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-xs font-medium">{row.occupancy}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {activeReport === 'checkin-checkout' && (
                    <Card className="border-0 shadow-sm">
                        <CardHeader className="border-b">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-base">Check-in / Check-out Report</CardTitle>
                                <div className="flex flex-wrap justify-end gap-2">
                                    <Button variant="outline" size="sm" className="gap-1"><Printer className="h-3.5 w-3.5" />Print</Button>
                                    <Button variant="outline" size="sm" className="gap-1"><Download className="h-3.5 w-3.5" />Excel</Button>
                                    <Button variant="outline" size="sm" onClick={() => setActiveReport(null)}>Close</Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-4">
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={checkInOutData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="checkIn" fill="#10B981" name="Check-in" radius={[3, 3, 0, 0]} />
                                    <Bar dataKey="checkOut" fill="#EF4444" name="Check-out" radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                )}

                {/* Reports Grid */}
                <div>
                    <h2 className="text-sm font-semibold text-gray-700 mb-3">
                        {filteredReports.length} reports available
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {filteredReports.map(report => (
                            <Card key={report.id} className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer group">
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className={`w-10 h-10 rounded-lg ${report.color} flex items-center justify-center ${report.iconColor}`}>
                                            {report.icon}
                                        </div>
                                        {report.badge && (
                                            <Badge
                                                className={`text-xs ${report.badge === 'Popular' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                                    report.badge === 'Live' ? 'bg-green-100 text-green-700 border-green-200' :
                                                        'bg-purple-100 text-purple-700 border-purple-200'
                                                    }`}
                                                variant="outline"
                                            >
                                                {report.badge}
                                            </Badge>
                                        )}
                                    </div>
                                    <h3 className="text-sm font-semibold text-gray-900 mb-1">{report.title}</h3>
                                    <p className="text-xs text-gray-500 leading-relaxed mb-3">{report.description}</p>
                                    <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                                        <span>{report.category}</span>
                                        <span>Updated {report.lastGenerated}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            className="flex-1 h-7 text-xs gap-1"
                                            style={{ background: '#5B9BD5' }}
                                            onClick={() => handleGenerate(report.id)}
                                            disabled={generatingReport === report.id}
                                        >
                                            {generatingReport === report.id ? (
                                                <><RefreshCw className="h-3 w-3 animate-spin" />Generating...</>
                                            ) : (
                                                <><Eye className="h-3 w-3" />View</>
                                            )}
                                        </Button>
                                        <Button variant="outline" size="sm" className="h-7 w-7 p-0">
                                            <Download className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
